"use client";

import { ArkErrors, type } from "arktype";

const UrlPathSchema = type("string").pipe((str) =>
  str.startsWith("/") ? (str as `/${string}`) : (`/${str}` as `/${string}`),
);

const ClientTypeSchema = type("'web' | 'desktop' | 'mobile' | 'ssr'");
type ClientType = typeof ClientTypeSchema.infer;

const determineClientType = (): ClientType => {
  // TODO: need to make sure we have navigator.useragent polyfill in react native
  if (
    typeof navigator !== "undefined" &&
    navigator.userAgent.includes("ReactNative")
  ) {
    return "mobile";
  } else if (typeof window === "undefined") {
    return "ssr";
  }
  // check for electron instance
  else if (
    typeof window !== "undefined" &&
    (window as any).process?.type === "renderer"
  ) {
    return "desktop";
  }
  return "web";
};

const clientType = ClientTypeSchema(determineClientType());
if (clientType instanceof ArkErrors) {
  throw new Error(
    `Error determining client type in tx-core env: ${clientType.toString()}`,
  );
}

const envSchema = type({
  env: "'development' | 'qa' | 'production' | 'test'",
  client: ClientTypeSchema,
  trace: "boolean = false",
  persistLocally: "boolean", // when false, no data is saved/restored from secondary storage (e.g. when hydrating from next).
  baseUrl: "string.url",
  authApiPath: UrlPathSchema,
  rpcApiPath: UrlPathSchema,
  thumbmarkApiKey: "string",
});

export type Env = typeof envSchema.infer;

function getBaseUrl(): string {
  if (typeof window !== "undefined") return window.location.origin;
  return "http://localhost:3000";
  // return "https://tilliX.app"
}

// TODO: come back to this with correct default values
function createDefaultEnv(): Env {
  const _env = envSchema({
    env: "development",
    client: "web",
    trace: false,
    persistLocally: false,
    baseUrl: getBaseUrl(),
    authApiPath: "/api/auth",
    rpcApiPath: "/api/rpc",
    thumbmarkApiKey: "26fb3e6e8af977dc511733e1419dfed7",
  });
  if (_env instanceof ArkErrors) {
    throw new Error(
      `Error creating default env for tx-core: ${_env.toString()}`,
    );
  }
  return _env;
}

let envState: Env = createDefaultEnv();

export function getEnv(): Env {
  return envState;
}

export function setEnv(partial: Partial<Env>): void {
  const merged = { ...envState, ...partial };
  const _env = envSchema(merged);
  if (_env instanceof ArkErrors) {
    throw new Error(`setEnv validation error for tx-core: ${_env.toString()}`);
  }
  envState = _env;
}

export function isProduction(): boolean {
  return envState.env === "production";
}

export function isDevelopment(): boolean {
  return envState.env === "development";
}

export function isMobileClient(): boolean {
  return envState.client === "mobile";
}
